import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../Common/Configurations/APIConfig';

const CustomerProfile = () => {
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        phoneNumber: '',
        city: '',
        address: '',
        role: '',
        isActive: true,
        userName: ''
    });
    const [formData, setFormData] = useState({ ...profileData });
    const userId = localStorage.getItem('userId');
    
    useEffect(() => {
        if (userId) {
            fetchCustomerProfile();
        }
    }, [userId]);

    const fetchCustomerProfile = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${config.APIACTIVEURL}${config.GETUSERPROFILE}/${userId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.data.success) {
                setProfileData(response.data.data);
                setFormData(response.data.data);
            } else {
                alert('Customer not found');
                navigate('/');
            }
        } catch (error) {
            console.error('Error fetching customer profile:', error);
            alert('Failed to load customer profile. Please try again.');
            navigate('/');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setSaving(true);

            const updateData = {
                userId: userId,
                name: formData.name,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                city: formData.city,
                address: formData.address
            };

            const response = await axios.put(
                `${config.APIACTIVEURL}${config.UPDATEUSERPROFILE}`,
                updateData,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setProfileData(response.data.data);
                setFormData(response.data.data);
                setIsEditing(false);
                alert('Customer updated successfully!');
            } else {
                alert(response.data.message || 'Failed to update customer.');
            }
        } catch (error) {
            console.error('Error updating customer:', error);
            alert(error.response?.data?.message || 'An error occurred while updating customer.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData({ ...profileData });
        setIsEditing(false);
    };

    if (loading) {
        return (
            <div className="container-fluid py-4" style={{ paddingTop: '100px' }}>
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading customer profile...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4" style={{ paddingTop: '100px' }}>
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-0 py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h3 className="mb-0 fw-bold text-dark">Customer Profile</h3>
                                </div>
                                <div>
                                    {!isEditing ? (
                                        <button 
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={() => setIsEditing(true)}
                                        >
                                            <i className="ri-edit-line me-1"></i> Edit Profile
                                        </button>
                                    ) : (
                                        <div className="d-flex gap-2">
                                            <button 
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={handleCancel}
                                                disabled={saving}
                                            >
                                                Cancel
                                            </button>
                                            <button 
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={handleSubmit}
                                                disabled={saving}
                                            >
                                                {saving ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-1"></i> Save Changes
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    {/* Left Column */}
                                    <div className="col-lg-4">
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-body text-center">
                                                <div className="position-relative d-inline-block mb-3">
                                                    <div className="rounded-circle overflow-hidden" 
                                                         style={{ 
                                                             width: '150px', 
                                                             height: '150px', 
                                                             margin: '0 auto'
                                                         }}>
                                                        <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-primary bg-opacity-10">
                                                            <i className="ri-user-line text-primary" style={{ fontSize: '60px' }}></i>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        className="form-control text-center fw-bold mb-2"
                                                        required
                                                        disabled={saving}
                                                    />
                                                ) : (
                                                    <h4 className="mb-2">{profileData.name}</h4>
                                                )}
                                                <p className="text-muted mb-3">
                                                    <span className="badge bg-info">
                                                        {profileData.role}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>

                                        {/* Status Card */}
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-white">
                                                <h5 className="mb-0 fw-bold">Account Status</h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="mb-3">
                                                    <label className="form-label text-muted small">Status</label>
                                                    <p className="mb-0 fw-medium">
                                                        {profileData.isActive ? (
                                                            <span className="badge bg-success">Active</span>
                                                        ) : (
                                                            <span className="badge bg-danger">Inactive</span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label text-muted small">Username</label>
                                                    <p className="mb-0 fw-medium">{profileData.userName}</p>
                                                </div>
                                               
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column - Customer Details */}
                                    <div className="col-lg-8">
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-header bg-white">
                                                <h5 className="mb-0 fw-bold">Customer Information</h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label fw-medium">Full Name</label>
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                name="name"
                                                                value={formData.name}
                                                                onChange={handleInputChange}
                                                                className="form-control"
                                                                required
                                                                disabled={saving}
                                                            />
                                                        ) : (
                                                            <p className="mb-0">{profileData.name}</p>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label fw-medium">Email</label>
                                                        {isEditing ? (
                                                            <input
                                                                type="email"
                                                                name="email"
                                                                value={formData.email}
                                                                onChange={handleInputChange}
                                                                className="form-control"
                                                                required
                                                                disabled={saving}
                                                            />
                                                        ) : (
                                                            <p className="mb-0">{profileData.email}</p>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label fw-medium">Phone Number</label>
                                                        {isEditing ? (
                                                            <input
                                                                type="tel"
                                                                name="phoneNumber"
                                                                value={formData.phoneNumber}
                                                                onChange={handleInputChange}
                                                                className="form-control"
                                                                disabled={saving}
                                                            />
                                                        ) : (
                                                            <p className="mb-0">{profileData.phoneNumber || 'Not provided'}</p>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label fw-medium">City</label>
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                name="city"
                                                                value={formData.city}
                                                                onChange={handleInputChange}
                                                                className="form-control"
                                                                disabled={saving}
                                                            />
                                                        ) : (
                                                            <p className="mb-0">{profileData.city || 'Not provided'}</p>
                                                        )}
                                                    </div>
                                                    <div className="col-12 mb-3">
                                                        <label className="form-label fw-medium">Address</label>
                                                        {isEditing ? (
                                                            <textarea
                                                                name="address"
                                                                value={formData.address}
                                                                onChange={handleInputChange}
                                                                className="form-control"
                                                                rows="3"
                                                                disabled={saving}
                                                            />
                                                        ) : (
                                                            <p className="mb-0">{profileData.address || 'Not provided'}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerProfile;